/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Vu Dinh Luan (vdluan@rsupartners.com)
 *
 * @description
 *
 * @created at Mon Dec 18 2024
 **/
import React, { useRef } from "react";
import { Subscription, timer } from "rxjs";

import { _P, _T } from "@rfice/language";
import { Typography, Row } from "@/frameworks/designStuff/components";
import { usePopover } from "@/frameworks/core/hooks";
import { GROUP_POPOVER_ID, ZIndex } from "@/frameworks/core/commons";
import { MiniProfile } from "@/frameworks/core/components/MiniProfile";
import { ChronoUtils, SwitchCase } from "@/frameworks/libraries";
import UserInfo from "../UserInfo";

interface CreateSystemChatProps {
	systemForm: ChatServices.Format2.System.Create | ChatServices.Format2.System.MemberAdded;
	type: "add" | "create";
	createAt?: number;
}

const CreateSystemChat = ({ systemForm, type, createAt }: CreateSystemChatProps) => {
	const popover = usePopover();
	const mouseEnterTimer = useRef<Subscription | null>(null);

	const showMiniProfile = ({ userID, anchorEl }: { userID: string; anchorEl: Element }) => {
		popover.show({
			content: (
				<div
					onMouseLeave={() => popover.hide(GROUP_POPOVER_ID.AVATAR_MINI_PROFILE)}
					style={{ width: "300px", height: "200px", paddingBottom: "10px" }}
				>
					<MiniProfile userID={userID} />
				</div>
			),
			popoverID: GROUP_POPOVER_ID.AVATAR_MINI_PROFILE,
			uniqueID: userID,
			direction: {
				x: "center",
				y: "top"
			},
			position: {
				x: 0,
				y: -155
			},
			anchorEl: anchorEl,
			zIndex: ZIndex.POPUP
		});
	};

	const mouseOver = (ev: React.MouseEvent) => {
		const target = ev.target as HTMLElement;
		mouseEnterTimer.current = timer(500).subscribe(() => {
			if (target instanceof HTMLElement) {
				const user = target.dataset.user;
				if (!user) return;

				showMiniProfile({ userID: user, anchorEl: target });
			}
		});
	};

	const mouseOut = () => {
		mouseEnterTimer.current?.unsubscribe();
		mouseEnterTimer.current = null;
	};

	return (
		<Typography.div
			typoSize="Body2"
			style={{
				flexWrap: "wrap",
				fontWeight: "bold",
				borderRadius: "12px",
				padding: "8px 16px",
				backgroundColor: "transparent",
				margin: "4px 0"
			}}
			onMouseOut={mouseOut}
			onMouseOver={mouseOver}
		>
			{systemForm.data?.invitee?.length === 0 ? (
				<>{_T("나와의 채팅을 시작하였습니다.")}</>
			) : (
				<SwitchCase
					condition={type}
					cases={{
						create: (
							<Row gap={10} verticalAlign="center">
								<Typography.div typoSize="Title2" style={{ wordBreak: "break-all" }}>
									{_P("{{inviter}} 님이 {{invitees}} 님과 대화를 시작하였습니다.", {
										inviter: `<span data-user="${systemForm.data.inviter.memberId}">${systemForm.data.inviter.memberName}</span>`,
										invitees: systemForm.data.invitee.map(
											(el) =>
												`<span style="color: #0D94E0; cursor: pointer" data-user="${el.memberId}">${el.memberName}</span>`
										)
									})}
								</Typography.div>
							</Row>
						),
						add: (
							<>
								<UserInfo
									id={systemForm.data.inviter.memberId}
									variant={"transparent"}
									date={ChronoUtils.formatTimeAgo(createAt || 0)}
								/>
								<Typography.div typoSize="Body2" css={{ marginLeft: 42 }}>
									{_P("{{inviter}} 님이 {{invitees}} 님을 추가하였습니다.", {
										inviter: `<span data-user="${systemForm.data.inviter.memberId}">${systemForm.data.inviter.memberName}</span>`,
										invitees: systemForm.data.invitee.map(
											(el) =>
												`<span style="color: #0D94E0; cursor: pointer" data-user="${el.memberId}">@${el.memberName}</span>`
										)
									})}
								</Typography.div>
							</>
						)
					}}
				/>
			)}
		</Typography.div>
	);
};

export default CreateSystemChat;
